import * as React from "react";
import styled, { css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { IfPermitted, PaginatedList, SearchForm, Spinner } from "components/common";
import { Button, ButtonGroup, Col, Row, Table } from "components/bootstrap";
import QueryHelper from "components/common/QueryHelper";
import ForwarderEntry from "./ForwarderEntry";
import CreateForwarderButton from "./CreateForwarderButton";
import { StyledSearchFormWrapper, StyledToolbar } from "../wizard/StyledWizardComponents";
import { StyledSortIcon } from "../CommonStyledComponents";
const StyledTable = styled(Table)`
  tr > th {
    width: 15%;

    &.rowTitle {
      width: 25%;
    }
    &.rowStatus {
      width: 120px;
      min-width: 120px;
    }
    &.rowDescription {
      width: 20%;
    }
    &.rowVersion {
      width: 150px;
      min-width: 150px;
    }
    &.rowMetrics {
      width: 10%;
    }
    &.rowActions {
      width: 160px;
      min-width: 160px;
    }
  }
`;
const ResponsiveTable = styled.div.attrs(() => ({
  className: "table-responsive"
}))(
  ({ theme }) => css`
    @media (max-width: ${theme.breakpoints.max.md}) {
      .dropdown-menu {
        position: static !important;
      }
    }
    @media (min-width: ${theme.breakpoints.min.md}) {
      overflow: inherit;
    }
  `
);
const StyledSubHeader = styled.span`
  margin-left: 0.5em;
`;
const StyledButtonGroup = styled(ButtonGroup)`
  margin-bottom: 10px;
`;
const ForwardersTable = ({
  forwarders,
  inputProfiles
}) => {
  const { t } = useTranslation("enterprise_forwarder");
  if (forwarders.length === 0) {
    return /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", { colSpan: 100 }, t("forwarders_list.no_match")));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, forwarders.map((forwarder) => {
    const inputProfile = inputProfiles.find((i) => i.id === forwarder.input_profile_id);
    return /* @__PURE__ */ React.createElement(ForwarderEntry, { key: forwarder.id, forwarder, inputProfile: inputProfile || null });
  }));
};
const QUERY_EXAMPLE_LOCAL = "local";
const QUERY_EXAMPLE_TITLE_LOCAL = "title:local";
const QUERY_EXAMPLE_TITLE_DATABASE = "title:database";
const QueryExamples = () => {
  const { t } = useTranslation("enterprise_forwarder");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("query_help.find_by_title"), "\xA0", /* @__PURE__ */ React.createElement("kbd", null, QUERY_EXAMPLE_TITLE_LOCAL), "\xA0", /* @__PURE__ */ React.createElement("kbd", null, QUERY_EXAMPLE_TITLE_DATABASE)), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "query_help.without_field_name", components: { code: /* @__PURE__ */ React.createElement("code", null) } }), " ", /* @__PURE__ */ React.createElement("kbd", null, QUERY_EXAMPLE_LOCAL), " ", t("query_help.is_the_same_as"), "\xA0", /* @__PURE__ */ React.createElement("kbd", null, QUERY_EXAMPLE_TITLE_LOCAL)));
};
const HelpPopover = () => {
  const { t } = useTranslation("enterprise_forwarder");
  return /* @__PURE__ */ React.createElement(
    QueryHelper,
    {
      entityName: "Forwarder",
      example: /* @__PURE__ */ React.createElement(QueryExamples, null),
      commonFields: ["title", "description"],
      fieldMap: {
        hostname: t("forwarders_list.field_hostname"),
        version: t("forwarders_list.field_version")
      }
    }
  );
};
const ForwardersListComponent = ({
  forwarders,
  inputProfiles,
  isLoading = false,
  onQueryChange = () => {
  },
  onSortChange = () => {
  },
  onStateFilterChange = (_filter) => {
  },
  pagination
}) => {
  const { t } = useTranslation("enterprise_forwarder");
  const { total, query, sortByField, order, stateFilter } = pagination;
  const _handleSortChange = (field) => {
    let nextOrder = "asc";
    if (sortByField === field) {
      nextOrder = order === "asc" ? "desc" : "asc";
    }
    onSortChange(field, nextOrder);
  };
  const _handleStateFilterChange = (name) => {
    onStateFilterChange(name);
  };
  const tableHeaders = [
    { sortable: true, field: "title", label: t("forwarders_list.columns.title"), className: "rowTitle" },
    { sortable: true, field: "state", label: t("forwarders_list.columns.status"), className: "rowStatus" },
    {
      sortable: true,
      field: "description",
      label: t("forwarders_list.columns.description"),
      className: "rowDescription"
    },
    { sortable: true, field: "hostname", label: t("forwarders_list.columns.hostname"), className: "rowHostname" },
    { sortable: true, field: "version", label: t("forwarders_list.columns.version"), className: "rowVersion" },
    {
      sortable: false,
      field: void 0,
      label: t("forwarders_list.columns.input_profile"),
      className: "rowInputProfile"
    },
    { sortable: false, field: void 0, label: t("forwarders_list.columns.metrics"), className: "rowMetrics" },
    { sortable: false, field: void 0, label: t("forwarders_list.columns.actions"), className: "rowActions" }
  ];
  const queryHelp = /* @__PURE__ */ React.createElement(HelpPopover, null);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement("h2", null, t("forwarders_list.heading"), /* @__PURE__ */ React.createElement("small", null, /* @__PURE__ */ React.createElement(StyledSubHeader, null, t("forwarders_list.total", { total })), /* @__PURE__ */ React.createElement(StyledSubHeader, null, isLoading && /* @__PURE__ */ React.createElement(Spinner, { text: "" })))), /* @__PURE__ */ React.createElement(StyledSearchFormWrapper, null, /* @__PURE__ */ React.createElement(SearchForm, { query, queryHelpComponent: queryHelp, onSearch: onQueryChange, onReset: onQueryChange }, /* @__PURE__ */ React.createElement(StyledToolbar, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "forwarders:create" }, /* @__PURE__ */ React.createElement(CreateForwarderButton, { buttonTitle: t("forwarders_list.new_forwarder") }))))), /* @__PURE__ */ React.createElement(StyledButtonGroup, null, /* @__PURE__ */ React.createElement(
    Button,
    {
      active: stateFilter === "connected",
      name: "connected",
      bsSize: "sm",
      onClick: () => _handleStateFilterChange("connected")
    },
    t("forwarders_list.filter_connected")
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      active: stateFilter === "disconnected",
      name: "disconnected",
      bsSize: "sm",
      onClick: () => _handleStateFilterChange("disconnected")
    },
    t("forwarders_list.filter_disconnected")
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      active: stateFilter === "any",
      name: "any",
      bsSize: "sm",
      onClick: () => _handleStateFilterChange("any")
    },
    t("forwarders_list.filter_any")
  )), /* @__PURE__ */ React.createElement(PaginatedList, { totalItems: total }, /* @__PURE__ */ React.createElement(ResponsiveTable, null, /* @__PURE__ */ React.createElement(StyledTable, null, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, tableHeaders.map(
    ({ sortable, field, label, className }) => sortable && field ? /* @__PURE__ */ React.createElement("th", { key: field, className }, label, /* @__PURE__ */ React.createElement(
      StyledSortIcon,
      {
        onChange: () => _handleSortChange(field),
        activeDirection: sortByField === field ? order : null,
        ascId: "asc",
        descId: "desc"
      }
    )) : /* @__PURE__ */ React.createElement("th", { key: label, className }, label)
  ))), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement(ForwardersTable, { forwarders, inputProfiles }))))))));
};
export default ForwardersListComponent;
